import { rotatePoint } from "lib/geometry/rotation";

interface Point {
  x: number;
  y: number;
}

interface ElementData {
  uniqueId: string;
  type: string;
  left: number;
  top: number;
  width: number;
  height: number;
  angle?: number;
  isHidden?: boolean;
  groupId?: string;
  elementsOrder?: string[];
  name?: string;
  value?: string;
}

interface DesignData {
  elements: Record<string, ElementData>;
  pages: Record<
    string,
    {
      elementsOrder: string[];
    }
  >;
  getElement: (id: string) => ElementData | undefined;
}

export interface OverlappingElement {
  element: ElementData;
  groupId: string | null;
}

function isPointInRotatedRect(point: Point, element: ElementData): boolean {
  const centerX = element.left + element.width / 2;
  const centerY = element.top + element.height / 2;
  const angle = element.angle || 0;

  // Un-rotate the point around the element's center by the negative of the element's angle
  const unrotated = rotatePoint(
    point.x,
    point.y,
    centerX,
    centerY,
    -angle
  ) as Point;

  // Check if the un-rotated point is within the axis-aligned bounding box
  return (
    unrotated.x >= element.left &&
    unrotated.x <= element.left + element.width &&
    unrotated.y >= element.top &&
    unrotated.y <= element.top + element.height
  );
}

/**
 * Returns all elements at a given design-space point, ordered from top (front) to bottom (back).
 * Excludes background and hidden elements.
 * For groups, tests against children (not the group container itself).
 */
export function getElementsAtPoint({
  designData,
  pageId,
  point
}: {
  designData: DesignData;
  pageId: string;
  point: Point;
}): OverlappingElement[] {
  const page = designData.pages[pageId];
  if (!page || !page.elementsOrder) return [];

  const results: OverlappingElement[] = [];

  // Iterate in reverse (top to bottom in z-order, since elementsOrder is bottom-to-top)
  for (let i = page.elementsOrder.length - 1; i >= 0; i--) {
    const elementId = page.elementsOrder[i] as string;
    const element = designData.getElement(elementId);
    if (!element) continue;

    if (element.type === "background") continue;
    if (element.isHidden) continue;

    if (element.type === "group" && element.elementsOrder) {
      // For groups, test against children in reverse order (top to bottom within group)
      const groupElementsOrder = element.elementsOrder;
      for (let j = groupElementsOrder.length - 1; j >= 0; j--) {
        const childId = groupElementsOrder[j] as string;
        const child = designData.getElement(childId);
        if (!child) continue;
        if (child.isHidden) continue;

        if (isPointInRotatedRect(point, child)) {
          results.push({ element: child, groupId: element.uniqueId });
        }
      }
    } else {
      if (isPointInRotatedRect(point, element)) {
        results.push({ element, groupId: null });
      }
    }
  }

  return results;
}
